import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const API_ENDPOINTS = {
  campus: "/api/campus-feed",
  anonymous: "/api/anonymous-feed",
};

const PAGE_SIZE = 15;

function safeFormatDate(createdAt) {
  if (!createdAt) return "";
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return String(createdAt);

  const now = Date.now();
  const diff = now - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return d.toLocaleDateString();
}

export default function FeedsCore({
  user,
  activeTab, // "campus" | "anonymous"
  setActiveTab, // (şu an burada kullanılmıyor ama dışarıdan kontrol için kalsın)
  composeOpen, // true => form açık, false => sadece liste
  showTopbar = false,
  onGoProfile,
  onGoDashboard,
  campusViewMode = "flow",
  isMobile = false,
  renderComposeOnly = false,
  renderFeedOnly = false,
  onPostCreated,
  refreshKey = 0,
}) {
  const [csrfToken, setCsrfToken] = useState("");

  const [feedData, setFeedData] = useState({
    campus: { loading: true, posts: [], error: "", hasMore: true, loadingMore: false },
    anonymous: { loading: false, posts: [], error: "", hasMore: true, loadingMore: false },
  });

  const [openComments, setOpenComments] = useState({
    campus: null,
    anonymous: null,
  });

  const [campusText, setCampusText] = useState("");
  const [campusImage, setCampusImage] = useState(null);
  const [campusPostError, setCampusPostError] = useState("");
  const [postingCampus, setPostingCampus] = useState(false);

  const [anonText, setAnonText] = useState("");
  const [anonPostError, setAnonPostError] = useState("");
  const [postingAnon, setPostingAnon] = useState(false);

  const viewportRef = useRef(null);
  const loadingMoreRef = useRef(false);

  function fetchWithTimeout(url, options = {}, timeoutMs = 10000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal })
      .then((res) => {
        clearTimeout(timer);
        if (res.status === 401 || res.status === 403) {
          window.location.href = "/react#/login";
          throw new Error("Session expired.");
        }
        return res;
      })
      .catch((err) => { clearTimeout(timer); throw err.name === "AbortError" ? new Error("Request timed out.") : err; });
  }

  function preloadImages(posts, feedKey, maxWait = 3000) {
    const urls = [];
    for (const p of posts) {
      if (typeof p.imageUrl === "string" && p.imageUrl.trim() !== "") {
        urls.push(p.imageUrl);
      } else if (p.hasImage) {
        urls.push(`/post-image?id=${encodeURIComponent(p.id)}`);
      }
      if (feedKey === "campus" && p.authorUserId != null && Number(p.authorUserId) > 0) {
        urls.push(`/post-author-avatar?userId=${encodeURIComponent(String(p.authorUserId))}`);
      }
    }
    if (urls.length === 0) return Promise.resolve();
    const imgPromises = urls.map(
      (u) => new Promise((resolve) => {
        const img = new window.Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = u;
      })
    );
    return Promise.race([
      Promise.allSettled(imgPromises),
      new Promise((resolve) => setTimeout(resolve, maxWait)),
    ]);
  }

  async function loadFeed(feedKey, append = false) {
    if (append) {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setFeedData((prev) => ({
        ...prev,
        [feedKey]: { ...prev[feedKey], loadingMore: true },
      }));
    } else {
      setFeedData((prev) => ({
        ...prev,
        [feedKey]: { ...prev[feedKey], loading: true, error: "" },
      }));
    }

    try {
      const baseUrl = API_ENDPOINTS[feedKey];
      if (!baseUrl) throw new Error("Unknown feed.");

      const currentOffset = append ? feedData[feedKey].posts.length : 0;
      const url = `${baseUrl}?offset=${currentOffset}`;

      const res = await fetchWithTimeout(url, { credentials: "include", cache: "no-store" }, 10000);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json().catch(() => null);
      if (!data || !data.ok) throw new Error(data?.error || "Failed.");

      const newPosts = data.posts || [];
      const hasMore = data.hasMore !== false;

      // İlk yüklemede image'ları preload et (max 3sn bekle)
      if (!append && newPosts.length > 0) {
        await preloadImages(newPosts, feedKey, 3000);
      }

      setFeedData((prev) => ({
        ...prev,
        [feedKey]: {
          loading: false,
          loadingMore: false,
          posts: append ? [...prev[feedKey].posts, ...newPosts] : newPosts,
          error: "",
          hasMore,
        },
      }));

      if (data.csrfToken) setCsrfToken(String(data.csrfToken));
    } catch (err) {
      setFeedData((prev) => ({
        ...prev,
        [feedKey]: {
          ...prev[feedKey],
          loading: false,
          loadingMore: false,
          error: append ? prev[feedKey].error : (err?.message || "Network error."),
        },
      }));
    } finally {
      loadingMoreRef.current = false;
    }
  }

  // İlk yükleme + refreshKey değişince yeniden yükle
  useEffect(() => {
    loadFeed(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, refreshKey]);

  // Ref ile güncel feedData'ya eriş — scroll listener'ı her feedData değişiminde yeniden oluşturma
  const feedDataRef = useRef(feedData);
  feedDataRef.current = feedData;

  // Infinite scroll: sayfa sonuna yaklaşınca daha fazla yükle
  useEffect(() => {
    function handleScroll() {
      const current = feedDataRef.current[activeTab];
      if (!current.hasMore || current.loadingMore || current.loading) return;

      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;

      // Sayfanın altına 300px kala tetikle
      if (docHeight - scrollBottom < 300) {
        loadFeed(activeTab, true);
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function handleCampusPostSubmit(e) {
    e.preventDefault();
    const trimmed = (campusText || "").trim();

    if (!trimmed && !campusImage) {
      setCampusPostError("Please write something before posting.");
      return;
    }
    if (!csrfToken) {
      setCampusPostError("Security check is still initializing. Please wait and try again.");
      setPostingCampus(false);
      return;
    }
    if (postingCampus) return;

    try {
      setPostingCampus(true);
      setCampusPostError("");

      const formData = new FormData();
      formData.append("csrfToken", csrfToken);
      formData.append("text", trimmed);
      if (campusImage) formData.append("image", campusImage);

      const res = await fetchWithTimeout("/api/campus-feed", {
        method: "POST",
        credentials: "include",
        body: formData,
      }, 30000);

      const data = await res.json().catch(() => null);

      if (!res.ok || !data || !data.ok) {
        const errMap = {
          image_upload_failed: "Failed to upload image. Please try again.",
          empty_text: "Please write something before posting.",
          image_too_large: "Image is too large (max 5MB).",
        };
        setCampusPostError(errMap[data?.error] || data?.error || "Error while posting.");
        return;
      }

      setCampusText("");
      setCampusImage(null);
      await loadFeed("campus");
      if (onPostCreated) onPostCreated();
    } catch (err) {
      console.error("campus post error", err);
      setCampusPostError("Error while posting.");
    } finally {
      setPostingCampus(false);
    }
  }

  async function handleAnonPostSubmit(e) {
    e.preventDefault();
    const trimmed = (anonText || "").trim();

    if (!trimmed) {
      setAnonPostError("Please write something before posting.");
      return;
    }
    if (!csrfToken) {
      setAnonPostError("Security check is still initializing. Please wait and try again.");
      setPostingAnon(false);
      return;
    }
    if (postingAnon) return;

    try {
      setPostingAnon(true);
      setAnonPostError("");

      const res = await fetchWithTimeout("/api/anonymous-feed", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: trimmed, csrfToken }),
      }, 15000);

      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.ok) {
        setAnonPostError(data?.error || "Error while posting.");
        return;
      }

      setAnonText("");
      await loadFeed("anonymous");
      if (onPostCreated) onPostCreated();
    } catch (err) {
      console.error("anon post error", err);
      setAnonPostError("Post error.");
    } finally {
      setPostingAnon(false);
    }
  }

  if (!user) return <div className="af-container">Login required.</div>;

  const current = feedData[activeTab];

  return (
    <div className="af-container">
      {showTopbar && (
        <header className="af-topbar">
          <div style={{ fontWeight: 700, fontSize: 18 }}>GRADA · Feeds</div>

          <button
            className="af-profile-pill"
            type="button"
            onClick={() => (onGoProfile ? onGoProfile() : (window.location.href = "/react#/profile"))}
          >
            <AvatarWithFallback
              src="/avatar"
              name={user.name}
              className="af-profile-avatar"
            />
            <div className="af-profile-text">
              <div className="af-profile-name">{user.name}</div>
              <div className="af-profile-sub">{user.university}</div>
            </div>
          </button>
        </header>
      )}

      {!renderFeedOnly && composeOpen && (
        <>
          {activeTab === "campus" && (
            <section className="af-card">
              <h2 className="af-card-title">Share something with your campus</h2>

              {campusPostError && <div className="af-error-text">{campusPostError}</div>}

              <form onSubmit={handleCampusPostSubmit} encType="multipart/form-data">
                <textarea
                  className="af-textarea"
                  placeholder="What's going on on campus?"
                  value={campusText}
                  onChange={(e) => setCampusText(e.target.value)}
                  maxLength={2000}
                />

                <input
                  type="file"
                  accept="image/*"
                  className="af-input-file"
                  onChange={(e) =>
                    setCampusImage(e.target.files && e.target.files[0] ? e.target.files[0] : null)
                  }
                />

                <button type="submit" className="af-btn" disabled={postingCampus}>
                  {postingCampus ? "Posting..." : "Post"}
                </button>
              </form>
            </section>
          )}

          {activeTab === "anonymous" && (
            <section className="af-card">
              <h2 className="af-card-title">Share something freely.</h2>

              <div className="af-warning">
                <div className="af-warning-title">Important Notice</div>
                <div className="af-warning-text">
                  The content shared in this area is created by users. The platform does not verify
                  the accuracy of the content and does not support or endorse any claims.
                  <br />
                  <br />
                  Content reported as unlawful may be removed. In accordance with applicable laws,
                  user information may be disclosed to competent authorities upon a valid legal request.
                </div>
              </div>

              {anonPostError && <div className="af-error-text">{anonPostError}</div>}

              <form onSubmit={handleAnonPostSubmit}>
                <textarea
                  className="af-textarea"
                  placeholder="What's on your mind?"
                  value={anonText}
                  onChange={(e) => setAnonText(e.target.value)}
                  maxLength={2000}
                />

                <button type="submit" className="af-btn" disabled={postingAnon}>
                  {postingAnon ? "Posting..." : "Post"}
                </button>
              </form>
            </section>
          )}
        </>
      )}

      {!renderComposeOnly && (
        <div ref={viewportRef}>
          {current.loading && current.posts.length === 0 ? (
            <SkeletonFeed count={3} />
          ) : current.error && current.posts.length === 0 ? (
            <div className="af-error-text">{current.error}</div>
          ) : current.posts.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9ca3af" }}>No posts in this feed yet.</div>
          ) : (
            <>
              <FeedList
                feedKey={activeTab}
                posts={current.posts}
                csrfToken={csrfToken}
                user={user}
                setFeedData={setFeedData}
                openCommentsPostId={openComments[activeTab]}
                setOpenCommentsPostId={(postId) =>
                  setOpenComments((prev) => ({ ...prev, [activeTab]: postId }))
                }
                campusViewMode={campusViewMode}
                isMobile={isMobile}
              />
              {current.loadingMore && (
                <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                  <div className="feed-spinner" />
                </div>
              )}
              {!current.hasMore && current.posts.length > 0 && (
                <div style={{ textAlign: "center", padding: "16px 0", color: "#9ca3af", fontSize: 13 }}>
                  No more posts to show
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- SKELETON LOADING ---------------- */

function SkeletonFeed({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={`skel-${i}`} className="af-skeleton-post">
          <div className="af-skel-header">
            <div className="af-skel af-skel-avatar" />
            <div>
              <div className="af-skel af-skel-name" />
              <div className="af-skel af-skel-meta" />
            </div>
          </div>
          <div className="af-skel af-skel-text1" />
          <div className="af-skel af-skel-text2" />
          {i === 0 && <div className="af-skel af-skel-image" />}
          <div className="af-skel-actions">
            <div className="af-skel af-skel-pill" />
            <div className="af-skel af-skel-pill" />
          </div>
        </div>
      ))}
    </>
  );
}

/* ---------------- FEED LIST + POST ITEM ---------------- */

function FeedList({
  feedKey,
  posts,
  csrfToken,
  user,
  setFeedData,
  openCommentsPostId,
  setOpenCommentsPostId,
  campusViewMode,
  isMobile,
}) {
  const listRef = useRef(null);
  const postRefs = useRef([]);
  const currentIndexRef = useRef(0);
  const wheelLockRef = useRef(false);
  const touchStartYRef = useRef(0);
  const touchLockRef = useRef(false);
  const prevFocusModeRef = useRef(campusViewMode);

  const [sheetPostId, setSheetPostId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [reportModal, setReportModal] = useState(null);

  function showToast(message, type = "success") {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }

  postRefs.current = [];

  useEffect(() => {
    setSheetPostId(null);
  }, [feedKey]);

  function setPostRef(el, index) {
    postRefs.current[index] = el;
  }

  function clampIndex(index) {
    if (!posts.length) return 0;
    return Math.max(0, Math.min(index, posts.length - 1));
  }

  function getClosestPostIndex() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportCenter = viewportHeight / 2;

    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;

    postRefs.current.forEach((el, index) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const distance = Math.abs(cardCenter - viewportCenter);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  function scrollToPost(index, behavior = "smooth") {
    if (index < 0) {
      currentIndexRef.current = -1;
      window.scrollTo({
        top: 0,
        behavior,
      });
      return;
    }

    const nextIndex = clampIndex(index);
    const el = postRefs.current[nextIndex];
    if (!el) return;

    currentIndexRef.current = nextIndex;

    el.scrollIntoView({
      behavior,
      block: "center",
      inline: "nearest",
    });
  }

  function setPostComments(postId, comments) {
    setFeedData((prev) => {
      const nextPosts = prev[feedKey].posts.map((p) => {
        if (p.id !== postId) return p;
        return {
          ...p,
          comments: Array.isArray(comments) ? comments : [],
          commentsLoaded: true,
        };
      });
      return { ...prev, [feedKey]: { ...prev[feedKey], posts: nextPosts } };
    });
  }

  async function ensureCommentsLoaded(postId) {
    const post = posts.find((p) => p.id === postId);
    if (!post) return { ok: false, error: "Post not found." };
    if (post.commentsLoaded) return { ok: true };

    const endpoint =
      feedKey === "anonymous"
        ? `/api/anonymous-comments?postId=${encodeURIComponent(post.id)}&limit=50`
        : `/api/post-comments?postId=${encodeURIComponent(post.id)}&limit=50`;

    try {
      const res = await fetch(endpoint, { credentials: "include" });
      const data = await res.json().catch(() => null);

      if (!res.ok || !data || !data.ok) {
        return { ok: false, error: data?.error || `Failed (HTTP ${res.status})` };
      }

      setPostComments(post.id, data.comments || []);
      return { ok: true };
    } catch {
      return { ok: false, error: "Network error while loading comments." };
    }
  }

  useLayoutEffect(() => {
    if (feedKey !== "campus" || campusViewMode !== "focus" || isMobile) return;
    if (!posts.length) return;

    const prevMode = prevFocusModeRef.current;
    prevFocusModeRef.current = campusViewMode;

    const justEnteredFocus = prevMode !== "focus";
    if (!justEnteredFocus) return;

    const id = window.requestAnimationFrame(() => {
      const closest = getClosestPostIndex();
      currentIndexRef.current = closest;
      scrollToPost(closest, "auto");
    });

    return () => window.cancelAnimationFrame(id);
  }, [feedKey, posts.length, campusViewMode, isMobile]);

  useEffect(() => {
    prevFocusModeRef.current = campusViewMode;
  }, [campusViewMode]);

  useLayoutEffect(() => {
    if (feedKey !== "campus" || campusViewMode !== "focus" || isMobile) return;
    if (openCommentsPostId == null) return;

    const openIndex = posts.findIndex((p) => p.id === openCommentsPostId);
    if (openIndex < 0) return;

    const id = window.requestAnimationFrame(() => {
      scrollToPost(openIndex, "smooth");
    });

    return () => window.cancelAnimationFrame(id);
  }, [feedKey, campusViewMode, openCommentsPostId, posts, isMobile]);

  useEffect(() => {
    if (feedKey !== "campus" || campusViewMode !== "focus" || isMobile) return;

    const el = listRef.current;
    if (!el) return;

    function lockWheel() {
      wheelLockRef.current = true;
      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 420);
    }

    function lockTouch() {
      touchLockRef.current = true;
      window.setTimeout(() => {
        touchLockRef.current = false;
      }, 420);
    }

    function onWheel(e) {
      if (feedKey !== "campus" || campusViewMode !== "focus" || isMobile) return;
      if (!posts.length) return;

      const target = e.target;
      if (target instanceof HTMLElement) {
        const insideComments = target.closest(".af-comments");
        const insideFormField = target.closest(".af-comment-input, .af-textarea, input, textarea");

        if (insideComments || insideFormField) {
          return;
        }
      }

      if (wheelLockRef.current) {
        e.preventDefault();
        return;
      }

      const deltaY = e.deltaY;
      if (Math.abs(deltaY) < 8) return;

      e.preventDefault();

      const baseIndex = getClosestPostIndex();
      const direction = deltaY > 0 ? 1 : -1;

      let nextIndex = baseIndex + direction;

      if (direction < 0 && baseIndex === 0) {
        const firstPost = postRefs.current[0];
        if (firstPost) {
          const rect = firstPost.getBoundingClientRect();
          const viewportCenter =
            (window.innerHeight || document.documentElement.clientHeight || 0) / 2;
          const isFirstPostAlreadyCentered =
            Math.abs(rect.top + rect.height / 2 - viewportCenter) < 24;

          if (isFirstPostAlreadyCentered) {
            nextIndex = -1;
          }
        }
      }

      if (nextIndex >= posts.length) nextIndex = posts.length - 1;
      if (nextIndex < -1) nextIndex = -1;
      if (nextIndex === baseIndex) return;

      lockWheel();
      scrollToPost(nextIndex, "smooth");
    }

    function onTouchStart(e) {
      if (!e.touches || !e.touches.length) return;
      touchStartYRef.current = e.touches[0].clientY;
    }

    function onTouchMove(e) {
      const target = e.target;
      if (target instanceof HTMLElement) {
        const insideComments = target.closest(".af-comments");
        const insideFormField = target.closest(".af-comment-input, .af-textarea, input, textarea");

        if (insideComments || insideFormField) {
          return;
        }
      }

      if (touchLockRef.current) {
        e.preventDefault();
        return;
      }

      if (!e.touches || !e.touches.length) return;

      const currentY = e.touches[0].clientY;
      const diff = touchStartYRef.current - currentY;

      if (Math.abs(diff) < 40) return;

      e.preventDefault();

      const baseIndex = getClosestPostIndex();
      const direction = diff > 0 ? 1 : -1;
      let nextIndex = baseIndex + direction;

      if (direction < 0 && baseIndex === 0) {
        const firstPost = postRefs.current[0];
        if (firstPost) {
          const rect = firstPost.getBoundingClientRect();
          const viewportCenter =
            (window.innerHeight || document.documentElement.clientHeight || 0) / 2;
          const isFirstPostAlreadyCentered =
            Math.abs(rect.top + rect.height / 2 - viewportCenter) < 24;

          if (isFirstPostAlreadyCentered) {
            nextIndex = -1;
          }
        }
      }

      if (nextIndex >= posts.length) nextIndex = posts.length - 1;
      if (nextIndex < -1) nextIndex = -1;
      if (nextIndex === baseIndex) return;

      lockTouch();
      scrollToPost(nextIndex, "smooth");
      touchStartYRef.current = currentY;
    }

    function onKeyDown(e) {
      if (feedKey !== "campus" || campusViewMode !== "focus" || isMobile) return;
      if (!posts.length) return;

      const activeEl = document.activeElement;
      const isTyping =
        activeEl &&
        activeEl instanceof HTMLElement &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.isContentEditable);

      if (isTyping) return;

      if (
        e.key !== "ArrowDown" &&
        e.key !== "ArrowUp" &&
        e.key !== "PageDown" &&
        e.key !== "PageUp"
      ) {
        return;
      }

      e.preventDefault();

      const baseIndex = getClosestPostIndex();
      const direction = e.key === "ArrowDown" || e.key === "PageDown" ? 1 : -1;

      let nextIndex = baseIndex + direction;

      if (direction < 0 && baseIndex === 0) {
        const firstPost = postRefs.current[0];
        if (firstPost) {
          const rect = firstPost.getBoundingClientRect();
          const viewportCenter =
            (window.innerHeight || document.documentElement.clientHeight || 0) / 2;
          const isFirstPostAlreadyCentered =
            Math.abs(rect.top + rect.height / 2 - viewportCenter) < 24;

          if (isFirstPostAlreadyCentered) {
            nextIndex = -1;
          }
        }
      }

      if (nextIndex >= posts.length) nextIndex = posts.length - 1;
      if (nextIndex < -1) nextIndex = -1;
      if (nextIndex === baseIndex) return;

      scrollToPost(nextIndex, "smooth");
    }

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [feedKey, posts, campusViewMode, isMobile]);

  const likeBusyRef = useRef({});

  async function toggleLike(postId) {
    if (!csrfToken) return;
    if (likeBusyRef.current[postId]) return;
    likeBusyRef.current[postId] = true;

    const url = feedKey === "anonymous" ? "/like-anonymous-post" : "/like-post";

    try {
      const form = new URLSearchParams();
      form.append("postId", String(postId));
      form.append("csrfToken", String(csrfToken));

      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: form.toString(),
      });

      if (!res.ok) return;

      const data = await res.json().catch(() => null);
      if (!data || !data.ok) return;

      setFeedData((prev) => {
        const nextPosts = prev[feedKey].posts.map((p) =>
          p.id === postId ? { ...p, likedByMe: data.liked, likeCount: data.likeCount } : p
        );
        return { ...prev, [feedKey]: { ...prev[feedKey], posts: nextPosts } };
      });
    } catch (e) {
      console.error("like error", e);
    } finally {
      likeBusyRef.current[postId] = false;
    }
  }

  async function reportPost(postId, reason) {
    if (feedKey !== "anonymous") return;
    if (!csrfToken) return;

    try {
      const res = await fetch("/report-anonymous-post", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, reason, csrfToken }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data || !data.ok) {
        showToast(data?.error || "Report failed.", "error");
        return;
      }

      showToast("Thanks. Your report has been received.");
    } catch (e) {
      console.error("report error", e);
      showToast("Report failed.", "error");
    }
  }

  async function addComment(postId, text, opts = { appendIfLoaded: false }) {
    const trimmed = String(text || "").trim();
    if (!trimmed) return { ok: false };
    if (!csrfToken) return { ok: false };

    const url = feedKey === "anonymous" ? "/comment-anonymous-post" : "/comment-post";

    try {
      const form = new URLSearchParams();
      form.append("postId", String(postId));
      form.append("comment", trimmed);
      form.append("csrfToken", String(csrfToken));

      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: form.toString(),
      });

      if (!res.ok) return { ok: false };

      const data = await res.json().catch(() => null);
      if (!data || !data.ok) return { ok: false };

      setFeedData((prev) => {
        const nextPosts = prev[feedKey].posts.map((p) => {
          if (p.id !== postId) return p;

          const next = {
            ...p,
            commentCount:
              typeof data.commentCount === "number" ? data.commentCount : (p.commentCount || 0) + 1,
          };

          if (opts.appendIfLoaded && p.commentsLoaded) {
            const comments = Array.isArray(p.comments) ? p.comments : [];
            if (feedKey === "campus") {
              next.comments = [
                ...comments,
                {
                  id: data.commentId ?? data.id ?? null,
                  text: data.text || trimmed,
                  authorName: user?.name || "You",
                  isOwn: true,
                },
              ];
            } else {
              next.comments = [
                ...comments,
                {
                  id: data.commentId ?? data.id ?? null,
                  text: data.text || trimmed,
                  isOwn: true,
                },
              ];
            }
          }

          return next;
        });

        return { ...prev, [feedKey]: { ...prev[feedKey], posts: nextPosts } };
      });

      return { ok: true };
    } catch (e) {
      console.error("comment error", e);
      return { ok: false };
    }
  }

  function deletePost(postId) {
    if (!csrfToken) return;

    setConfirmModal({
      message: "Are you sure you want to delete this post?",
      onConfirm: async () => {
        setConfirmModal(null);
        const url = feedKey === "anonymous" ? "/delete-anonymous-post" : "/delete-post";

        try {
          const form = new URLSearchParams();
          form.append("id", String(postId));
          form.append("csrfToken", String(csrfToken));

          const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: form.toString(),
          });

          if (!res.ok) return;

          if (openCommentsPostId === postId) {
            setOpenCommentsPostId(null);
          }
          if (sheetPostId === postId) {
            setSheetPostId(null);
          }

          setFeedData((prev) => {
            const nextPosts = prev[feedKey].posts.filter((p) => p.id !== postId);
            return { ...prev, [feedKey]: { ...prev[feedKey], posts: nextPosts } };
          });
        } catch (e) {
          console.error("delete error", e);
        }
      },
    });
  }

  function deleteComment(postId, commentId) {
    if (!csrfToken) return;
    if (commentId == null) return;

    setConfirmModal({
      message: "Are you sure you want to delete this comment?",
      onConfirm: async () => {
        setConfirmModal(null);

        const endpoint =
          feedKey === "anonymous"
            ? "/delete-anonymous-comment"
            : "/delete-comment";

        const form = new URLSearchParams();
        form.append("commentId", String(commentId));
        form.append("csrfToken", String(csrfToken));

        try {
            const res = await fetch(endpoint, {
              method: "POST",
              credentials: "include",
              headers: {
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: form.toString(),
            });

            const data = await res.json().catch(() => null);

            if (!res.ok || !data || !data.ok) {
              showToast("Could not delete comment.", "error");
              return;
            }

            setFeedData((prev) => {
              const nextPosts = prev[feedKey].posts.map((p) => {
                if (p.id !== postId) return p;
                const comments = Array.isArray(p.comments) ? p.comments : [];
                const nextComments = comments.filter((c) => c.id !== commentId);
                return {
                  ...p,
                  comments: nextComments,
                  commentCount: typeof data.commentCount === "number" ? data.commentCount : p.commentCount,
                };
              });
              return { ...prev, [feedKey]: { ...prev[feedKey], posts: nextPosts } };
            });
        } catch (e) {
            console.error("delete comment error", e);
            showToast("Could not delete comment.", "error");
        }
      },
    });
  }

  function requestReport(postId) {
    setReportModal({ postId });
  }

  async function handleReportSubmit(reason) {
    if (!reportModal) return;
    const { postId } = reportModal;
    setReportModal(null);
    await reportPost(postId, reason);
  }

  const sheetPost = sheetPostId != null ? posts.find((p) => p.id === sheetPostId) || null : null;

  const useSnapClass = feedKey === "campus" && campusViewMode === "focus";

  return (
    <>
      <section
        ref={listRef}
        className={useSnapClass ? "af-feed-list af-feed-list--campus-snap" : "af-feed-list"}
      >
        {posts.map((p, index) => (
          <div
            key={p.id}
            id={`post-${p.id}`}
            ref={(el) => setPostRef(el, index)}
            className={useSnapClass ? "af-post-slot af-post-slot--campus" : "af-post-slot"}
          >
            <FeedPostItem
              post={p}
              user={user}
              feedKey={feedKey}
              onToggleLike={toggleLike}
              onAddComment={addComment}
              onDelete={deletePost}
              onDeleteComment={deleteComment}
              onReport={requestReport}
              onSetPostComments={setPostComments}
              onEnsureCommentsLoaded={ensureCommentsLoaded}
              commentsOpen={openCommentsPostId === p.id}
              setCommentsOpen={(open) => setOpenCommentsPostId(open ? p.id : null)}
              sheetPostId={sheetPostId}
              setSheetPostId={setSheetPostId}
              campusViewMode={campusViewMode}
              isMobile={isMobile}
            />
          </div>
        ))}
      </section>

      {sheetPost && (
        <CommentsSheet
          post={sheetPost}
          user={user}
          feedKey={feedKey}
          onClose={() => setSheetPostId(null)}
          onAddComment={addComment}
          onDeleteComment={deleteComment}
          onEnsureCommentsLoaded={ensureCommentsLoaded}
        />
      )}

      {toasts.length > 0 && <ToastContainer toasts={toasts} />}

      {confirmModal && (
        <ConfirmModal
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {reportModal && (
        <ReportModal
          onSubmit={handleReportSubmit}
          onCancel={() => setReportModal(null)}
        />
      )}
    </>
  );
}

function IcoHeart({ filled = false, className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor">
      <path
        d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoComment({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path
        d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoChevron({ up = false, className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor">
      {up ? (
        <path d="M6 15l6-6 6 6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M6 9l6 6 6-6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

export function AvatarWithFallback({ src, name, className }) {
  const [failed, setFailed] = useState(false);
  const initial = (name || "?").charAt(0).toUpperCase();

  if (failed || !src) {
    return (
      <div className={`af-avatar-fallback ${className || ""}`}>
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      className={className}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function PostImage({ src }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`af-post-image-wrapper${loaded ? "" : " af-img-placeholder"}`}>
      {!loaded && (
        <div className="af-img-spinner-wrap">
          <div className="feed-spinner" />
        </div>
      )}
      <img
        src={src}
        alt=""
        className="af-post-image"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
}

function FeedPostItem({
  post,
  user,
  feedKey,
  onToggleLike,
  onAddComment,
  onDelete,
  onDeleteComment,
  onReport,
  onSetPostComments,
  onEnsureCommentsLoaded,
  commentsOpen,
  setCommentsOpen,
  sheetPostId,
  setSheetPostId,
  campusViewMode,
  isMobile,
}) {
  const createdLabel = safeFormatDate(post.createdAt);

  const isOwnPost = !!(post.deletable || post.isOwnPost);
  const isAdmin = !!user?.isAdmin;
  const showDelete = feedKey === "anonymous" ? isAdmin || isOwnPost : isOwnPost;

  const hasImage =
    (typeof post.imageUrl === "string" && post.imageUrl.trim() !== "") || post.hasImage;

  const imageSrc =
    post.imageUrl && post.imageUrl.trim() !== ""
      ? post.imageUrl
      : `/post-image?id=${encodeURIComponent(post.id)}`;

  const showAvatar =
    feedKey === "campus" && post?.authorUserId != null && Number(post.authorUserId) > 0;

  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [commentText, setCommentText] = useState("");
  const [likePop, setLikePop] = useState(false);
  const [commentsRendered, setCommentsRendered] = useState(false);

  const useSheetComments = isMobile && feedKey === "campus" && campusViewMode === "focus";
  const sheetOpen = sheetPostId === post.id;

  useEffect(() => {
    if (commentsOpen) setCommentsRendered(true);
  }, [commentsOpen]);

  useEffect(() => {
    setCommentsError("");
    setCommentText("");
  }, [post.id]);

  async function toggleComments() {
    if (useSheetComments) {
      setSheetPostId(post.id);
      return;
    }

    const next = !commentsOpen;
    setCommentsOpen(next);

    if (!next) return;
    if (post.commentsLoaded) return;

    try {
      setCommentsLoading(true);
      setCommentsError("");

      const result = await onEnsureCommentsLoaded(post.id);
      if (!result?.ok) {
        setCommentsError(result?.error || "Failed to load comments.");
      }
    } finally {
      setCommentsLoading(false);
    }
  }

  async function likeClick() {
    if (!post.likedByMe) {
      setLikePop(true);
      window.setTimeout(() => setLikePop(false), 260);
    }
    onToggleLike(post.id);
  }

  async function submitInlineComment(e) {
    e.preventDefault();
    const t = (commentText || "").trim();
    if (!t) return;

    const r = await onAddComment(post.id, t, { appendIfLoaded: true });
    if (r.ok) setCommentText("");
  }

  return (
    <div className="af-post af-post--replit">
      <div className="af-post-header">
        <div className="af-post-head-left">
          {showAvatar ? (
            <>
              <AvatarWithFallback
                src={
                  post?.authorUserId
                    ? `/post-author-avatar?userId=${encodeURIComponent(String(post.authorUserId))}`
                    : ""
                }
                name={post.authorName}
                className="af-post-avatar"
              />
              <div>
                <div className="af-post-author">{post.authorName || "Unknown"}</div>
                <div className="af-post-meta">
                  {post.university} · {createdLabel}
                </div>
              </div>
            </>
          ) : (
            <div className="af-post-meta">
              {(post.authorName || "Someone") + (post.university ? ` · ${post.university}` : "")}
              {createdLabel ? ` · ${createdLabel}` : ""}
            </div>
          )}
        </div>

        {showDelete && (
          <button className="af-pill-btn" type="button" onClick={() => onDelete(post.id)}>
            Delete
          </button>
        )}
      </div>

      <div className="af-post-text">{post.text}</div>

      {hasImage && (
        <PostImage src={imageSrc} />
      )}

      <div className="af-actions-row">
        <button
          type="button"
          onClick={likeClick}
          className={`af-pill-btn af-like ${post.likedByMe ? "af-like--on" : ""}`}
        >
          <IcoHeart
            filled={!!post.likedByMe}
            className={`af-ico ${post.likedByMe ? "af-ico--fill" : ""} ${likePop ? "af-like-pop" : ""}`}
          />
          <span>({post.likeCount})</span>
        </button>

        <button
          type="button"
          onClick={toggleComments}
          className={`af-pill-btn af-comment ${commentsOpen || sheetOpen ? "af-comment--open" : ""}`}
        >
          <IcoComment className="af-ico" />
          <span>({post.commentCount})</span>
          {!useSheetComments && <IcoChevron up={commentsOpen} className="af-ico af-ico-chev" />}
        </button>

        {feedKey === "anonymous" && (
          <button
            type="button"
            className="af-pill-btn af-report"
            onClick={() => onReport(post.id)}
          >
            Report
          </button>
        )}
      </div>

      {!useSheetComments && (
        <div className={`af-comments ${commentsOpen ? "af-comments--open" : ""}`}>
          <div className="af-comments-inner">
            {commentsRendered && (
              <>
                {commentsLoading ? (
                  <div className="af-comments-status">Loading...</div>
                ) : commentsError ? (
                  <div className="af-error-text af-comments-status">{commentsError}</div>
                ) : (
                  <div className="af-comments-list">
                    {(post.comments || []).length === 0 ? (
                      <div className="af-comments-empty">No comments yet.</div>
                    ) : (
                      (post.comments || []).map((c, i) => {
                        const commentId = c.id ?? null;
                        const canDelete =
                          commentId != null &&
                          ((feedKey === "campus" && c.isOwn) ||
                            (feedKey === "anonymous" && (c.isOwn || user?.isAdmin)));

                        const initial = (() => {
                          const name =
                            feedKey === "anonymous"
                              ? "S"
                              : c.isOwn
                              ? (user?.name || "Y").charAt(0)
                              : (c.authorName || "U").charAt(0);
                          return String(name || "U").toUpperCase();
                        })();

                        const who =
                          feedKey === "anonymous"
                            ? "Someone"
                            : c.isOwn
                            ? user?.name || "You"
                            : c.authorName || "User";

                        return (
                          <div key={commentId ?? `i-${i}`} className="af-comment-item">
                            <div className="af-comment-bubble">{initial}</div>

                            <div className="af-comment-card">
                              <div className="af-comment-top">
                                <span className="af-comment-author">{who}</span>
                                {c.time ? <span className="af-comment-time">{c.time}</span> : null}
                              </div>
                              <div className="af-comment-text">{c.text}</div>
                            </div>

                            {canDelete && (
                              <button
                                type="button"
                                className="af-comment-del"
                                onClick={() => onDeleteComment(post.id, commentId)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                <form className="af-comment-compose" onSubmit={submitInlineComment}>
                  <div className="af-comment-me">
                    {(String(user?.name || "p").trim().charAt(0) || "p").toLowerCase()}
                  </div>

                  <div className="af-comment-inputwrap">
                    <input
                      className="af-comment-input"
                      type="text"
                      placeholder="Write a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      maxLength={500}
                    />
                    <button type="submit" className="af-comment-postbtn">
                      Post
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CommentsSheet({
  post,
  user,
  feedKey,
  onClose,
  onAddComment,
  onDeleteComment,
  onEnsureCommentsLoaded,
}) {
  const [loading, setLoading] = useState(!post?.commentsLoaded);
  const [error, setError] = useState("");
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!post) return;
      if (post.commentsLoaded) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      const result = await onEnsureCommentsLoaded(post.id);
      if (!alive) return;

      if (!result?.ok) {
        setError(result?.error || "Failed to load comments.");
      }
      setLoading(false);
    }

    run();

    return () => {
      alive = false;
    };
  }, [post, onEnsureCommentsLoaded]);

  useEffect(() => {
    if (!post) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [post, onClose]);

  if (!post) return null;

  async function submitComment(e) {
    e.preventDefault();
    const t = String(commentText || "").trim();
    if (!t) return;

    const r = await onAddComment(post.id, t, { appendIfLoaded: true });
    if (r.ok) {
      setCommentText("");
      setError("");
    }
  }

  const node = (
    <>
      <div className="af-comments-sheet-backdrop" onClick={onClose} />
      <div
        className="af-comments-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Comments"
      >
        <div className="af-comments-sheet-handle" />

        <div className="af-comments-sheet-header">
          <div className="af-card-title" style={{ margin: 0 }}>
            Comments ({post.commentCount || 0})
          </div>

          <button type="button" className="af-pill-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="af-comments-sheet-body">
          {loading ? (
            <div className="af-comments-status">Loading...</div>
          ) : error ? (
            <div className="af-error-text af-comments-status">{error}</div>
          ) : (
            <div className="af-comments-list">
              {(post.comments || []).length === 0 ? (
                <div className="af-comments-empty">No comments yet.</div>
              ) : (
                (post.comments || []).map((c, i) => {
                  const commentId = c.id ?? null;
                  const canDelete =
                    commentId != null &&
                    ((feedKey === "campus" && c.isOwn) ||
                      (feedKey === "anonymous" && (c.isOwn || user?.isAdmin)));

                  const initial = (() => {
                    const name =
                      feedKey === "anonymous"
                        ? "S"
                        : c.isOwn
                        ? (user?.name || "Y").charAt(0)
                        : (c.authorName || "U").charAt(0);
                    return String(name || "U").toUpperCase();
                  })();

                  const who =
                    feedKey === "anonymous"
                      ? "Someone"
                      : c.isOwn
                      ? user?.name || "You"
                      : c.authorName || "User";

                  return (
                    <div key={commentId ?? `sheet-i-${i}`} className="af-comment-item">
                      <div className="af-comment-bubble">{initial}</div>

                      <div className="af-comment-card">
                        <div className="af-comment-top">
                          <span className="af-comment-author">{who}</span>
                          {c.time ? <span className="af-comment-time">{c.time}</span> : null}
                        </div>
                        <div className="af-comment-text">{c.text}</div>
                      </div>

                      {canDelete && (
                        <button
                          type="button"
                          className="af-comment-del"
                          onClick={() => onDeleteComment(post.id, commentId)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <form className="af-comment-compose" onSubmit={submitComment} style={{ padding: "12px 16px 16px" }}>
          <div className="af-comment-me">
            {(String(user?.name || "p").trim().charAt(0) || "p").toLowerCase()}
          </div>

          <div className="af-comment-inputwrap">
            <input
              className="af-comment-input"
              type="text"
              placeholder="Write a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={500}
            />
            <button type="submit" className="af-comment-postbtn">
              Post
            </button>
          </div>
        </form>
      </div>
    </>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ---------------- TOAST + MODALS ---------------- */

function ToastContainer({ toasts }) {
  return ReactDOM.createPortal(
    <div className="af-toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`af-toast af-toast--${t.type}`}>
          {t.message}
        </div>
      ))}
    </div>,
    document.body
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <>
      <div className="af-modal-backdrop" onClick={onCancel} />
      <div className="af-modal" role="dialog" aria-modal="true">
        <div className="af-modal-body">{message}</div>
        <div className="af-modal-actions">
          <button type="button" className="af-modal-btn af-modal-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="af-modal-btn af-modal-btn--confirm" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function ReportModal({ onSubmit, onCancel }) {
  const [reason, setReason] = useState("");

  return ReactDOM.createPortal(
    <>
      <div className="af-modal-backdrop" onClick={onCancel} />
      <div className="af-modal" role="dialog" aria-modal="true">
        <div className="af-modal-title">Report this post</div>
        <textarea
          className="af-modal-textarea"
          placeholder="Why are you reporting this post?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          autoFocus
        />
        <div className="af-modal-actions">
          <button type="button" className="af-modal-btn af-modal-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="af-modal-btn af-modal-btn--confirm af-modal-btn--report"
            onClick={() => {
              if (!reason.trim()) return;
              onSubmit(reason.trim());
            }}
          >
            Report
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}